import React, { memo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  Icon,
  Stack,
  Tooltip,
  Badge,
  Box,
} from "investira.react.components";
import { validators, formats } from "investira.sdk";
import { Link } from "react-router-dom";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";
import ConcentracaoGraph from "./Concentracao/ConcentracaoGraph";
import { useConsultaContext } from "../../../../contexts/ConsultaContext";
import { PessoaImage } from "../../../../components/molecules";
import { displays } from "investira.react.lib";
import { PATHS } from "../../../../enums";

const DetalheTable = memo((props) => {
  const { detalhe, onSetEmissor } = props;

  const { state } = useConsultaContext();

  function agruparPorAtivoTipoId(pDetalhe) {
    // Verifica se o input é um array
    if (!Array.isArray(pDetalhe)) {
      console.error("O parâmetro deve ser um array.");
      return {};
    }

    const agrupado = {};

    pDetalhe.forEach((item) => {
      const tipoId = item.ativo_tipo_id;

      // Se o tipo não existir no objeto, cria um array vazio para ele
      if (!agrupado[tipoId]) {
        agrupado[tipoId] = [];
      }

      // Adiciona o item ao grupo correspondente
      agrupado[tipoId].push(item);
    });

    return agrupado;
  }

  function calcRangeEnquadramento(pMin = 0, pMax = 50) {
    return Math.abs(pMax - pMin);
  }

  function calcAlvoRange(
    pAlvo = null,
    pAlvoLimites = {},
    pDesenquadradoAlvo = null,
    pDesenquadrado = null
  ) {
    let targetColors = "white";

    if (!validators.isNull(pDesenquadrado)) {
      targetColors = "error.light";
    }

    if (!validators.isNull(pDesenquadradoAlvo)) {
      targetColors = "warning.light";
    }

    return {
      alvo: pAlvo,
      alvoLimites: pAlvoLimites,
      alvoWidth: pAlvoLimites?.maximo - pAlvoLimites.minimo,
      desenquadradoAlvo: !validators.isNull(pDesenquadradoAlvo),
      pDesenquadrado: !validators.isNull(pDesenquadrado),
      noAlvo: !validators.isNull(pDesenquadradoAlvo),
      targetColor: targetColors,
    };
  }

  function calcObservado(pObservado) {
    if (pObservado <= 0) {
      return "0%";
    }

    return `calc(${pObservado}% - 1px)`;
  }

  function getDetalheFiltrado(detalhe, state) {
    return state.exibir_desenquadrado
      ? detalhe.filter((x) => x.desenquadrado)
      : detalhe;
  }

  return (
    <TableContainer
      id="table-container"
      sx={{
        overflow: "auto",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Descrição</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              Mínimo (%)
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              Máximo (%)
            </TableCell>
            <TableCell align="right">Observado</TableCell>
            <TableCell></TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              Observado (%)
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              Alvo (%)
            </TableCell>
            <TableCell align="right">Desenquadrado</TableCell>
            <TableCell></TableCell>
            <TableCell align="left">Obs.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {detalhe.map((xItem) => {
            const xAlvoData = calcAlvoRange(
              xItem.alvo,
              xItem.alvo_limites,
              xItem.desenquadrado_alvo,
              xItem.desenquadrado
            );

            return (
              <React.Fragment key={crypto.randomUUID()}>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.secondary.dark,
                  }}
                >
                  <TableCell colSpan="3">
                    <b>{xItem?.descricao}</b>
                  </TableCell>
                  <TableCell align="right">{xItem.minimo}</TableCell>
                  <TableCell align="right">{xItem.maximo}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: xAlvoData.targetColor,
                    }}
                  >
                    {Boolean(xItem.financeiro) &&
                      formats.formatNumber(xItem.financeiro, 2, true)}
                  </TableCell>
                  <TableCell align="center"></TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: xAlvoData.targetColor,
                    }}
                  >
                    {xItem.observado &&
                      formats.formatNumber(xItem.observado, 6, true)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "info.main", fontWeight: 600 }}
                  >
                    {xItem.alvo}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: xAlvoData.targetColor,
                    }}
                  >
                    {xItem.desenquadrado &&
                      formats.formatNumber(xItem.desenquadrado, 2, true)}
                    {xItem.desenquadrado_alvo &&
                      formats.formatNumber(xItem.desenquadrado_alvo, 2, true)}
                  </TableCell>
                  <TableCell align="center">
                    {xItem.observado && (
                      <ConcentracaoGraph
                        range={calcRangeEnquadramento(
                          xItem.minimo,
                          xItem.maximo
                        )}
                        minimo={xItem.minimo}
                        alvo={xAlvoData}
                        observado={calcObservado(xItem.observado)}
                        targetColor={xAlvoData.targetColor}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>

                {!validators.isEmpty(xItem.criterio) && (
                  <TableRow>
                    <TableCell colSpan="12">
                      <Typography variant="caption" color={"textSecondary"}>
                        {xItem.criterio.join(", ")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {Object.entries(
                  agruparPorAtivoTipoId(
                    getDetalheFiltrado(xItem.detalhe, state)
                  )
                ).map(([xAtivoTipoId, xDetalhes]) => {
                  return (
                    <React.Fragment key={crypto.randomUUID()}>
                      {!validators.isNull(xAtivoTipoId) && (
                        <TableRow>
                          <TableCell colSpan="5" sx={{ pl: 4 }}>
                            <b>{ATIVO_TIPO[xAtivoTipoId]?.descricao}</b>
                          </TableCell>
                          <TableCell sx={{ pl: 4 }} align="right">
                            {Boolean(xItem.financeiro) &&
                              formats.formatNumber(xItem.financeiro, 2, true)}
                          </TableCell>
                          <TableCell colSpan="6" sx={{ pl: 4 }}></TableCell>
                        </TableRow>
                      )}

                      {xDetalhes.map((xAtivo) => {
                        const hasDesenquadrado =
                          !validators.isNull(xAtivo.desenquadrado) ||
                          !validators.isNull(xAtivo.desenquadrado_alvo);

                        const xEnquadramentoRange = calcRangeEnquadramento(
                          xItem.minimo || xAtivo.minimo,
                          xItem.maximo || xAtivo.maximo
                        );
                        const xAlvo = calcAlvoRange(
                          xAtivo.alvo,
                          xAtivo.alvo_limites,
                          xAtivo.desenquadrado_alvo,
                          xAtivo.desenquadrado
                        );
                        const xObservado = calcObservado(xAtivo.observado);
                        const isEnquadrado = validators.isNull(
                          xAtivo.desenquadrado
                        );

                        const xMercado = ATIVO_TIPO[xAtivoTipoId]?.mercado;
                        const xPath = xMercado
                          ? `/ativos${PATHS[xMercado.toLowerCase()].path}/${
                              xAtivo.ifeed_ativo_id
                            }`
                          : null;

                        return (
                          <TableRow>
                            <TableCell sx={{ pl: 6, width: "24px" }}>
                              {Boolean(xAtivo?.logo_pessoa_id) &&
                                !Boolean(xAtivo?.emissor) && (
                                  <Link
                                    to={`/empresas/${xAtivo.logo_pessoa_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <PessoaImage
                                      logoPessoaId={xAtivo.logo_pessoa_id}
                                      alt={xAtivo.pessoa_fantasia}
                                      hasLogo={Boolean(xAtivo?.logo_pessoa_id)}
                                      size={24}
                                    />
                                  </Link>
                                )}

                              {Boolean(xAtivo?.logo_pessoa_id) &&
                                Boolean(xAtivo?.emissor) && (
                                  <Tooltip
                                    title={
                                      xAtivo.emissor?.pessoa_fantasia || ""
                                    }
                                    arrow={true}
                                  >
                                    <span>
                                      <PessoaImage
                                        logoPessoaId={xAtivo.logo_pessoa_id}
                                        alt={xAtivo.pessoa_fantasia}
                                        hasLogo={Boolean(
                                          xAtivo?.logo_pessoa_id
                                        )}
                                        size={24}
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                            </TableCell>
                            <TableCell sx={{ color: xAlvo.targetColor }}>
                              {xAtivo?.pessoa_fantasia ? (
                                <Typography
                                  component={Link}
                                  to={`/empresas/${xAtivo.pessoa_id}`}
                                  color="primary"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  aria-label={`Abrir ${xAtivo?.pessoa_fantasia} em uma nova aba`}
                                >
                                  {xAtivo.pessoa_fantasia}
                                </Typography>
                              ) : (
                                <>{xAtivo?.pessoa_fantasia}</>
                              )}

                              {xPath ? (
                                <Badge
                                  badgeContent={"V" || xItem.marcacao}
                                  color="warning"
                                >
                                  <Box pr={2}>
                                    <Typography
                                      component={Link}
                                      to={xPath}
                                      color="primary"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="body2"
                                      aria-label={`Abrir ${xAtivo?.ativo} em uma nova aba`}
                                    >
                                      {xAtivo?.ativo}
                                    </Typography>
                                  </Box>
                                </Badge>
                              ) : (
                                <Badge
                                  badgeContent={xItem.marcacao}
                                  color="warning"
                                >
                                  <Box pr={2}>
                                    <Typography
                                      color="textPrimary"
                                      variant="body2"
                                    >
                                      {xAtivo.descricao || xAtivo?.ativo}
                                    </Typography>
                                  </Box>
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                whiteSpace: "nowrap",
                                textAlign: xAtivo?.cnpj ? "right" : "left",
                              }}
                            >
                              {xAtivo?.isin || displays.cnpj(xAtivo?.cnpj)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.secondary" }}
                            >
                              {xItem.minimo ?? xAtivo.minimo}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.secondary" }}
                            >
                              {xItem.maximo ?? xAtivo.maximo}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: xAlvo.targetColor,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="right"
                                gap={1}
                              >
                                {(xAtivo.financeiro || xAtivo.quantidade) &&
                                  formats.formatNumber(
                                    xAtivo.financeiro || xAtivo.quantidade,
                                    xAtivo.decimais || 0,
                                    true
                                  )}
                              </Stack>
                            </TableCell>

                            <TableCell align="center">
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="right"
                                gap={1}
                              >
                                {xAtivo.base && (
                                  <Tooltip
                                    title={
                                      <Stack px={1} py={1}>
                                        {Object.entries(xAtivo.base).map(
                                          ([key, value]) => (
                                            <Typography
                                              variant="caption"
                                              key={key}
                                            >
                                              <strong>{key}:</strong>{" "}
                                              {key === "Data"
                                                ? formats.formatDate(value)
                                                : typeof value === "object"
                                                ? Object.entries(value)
                                                    .map(([k, v]) => {
                                                      if (k === "CNPJ") {
                                                        return `${k}: ${displays.cnpj(
                                                          v
                                                        )}`;
                                                      }
                                                      if (
                                                        k === "ON" ||
                                                        k === "PN"
                                                      ) {
                                                        return `${k}: ${formats.formatNumber(
                                                          v,
                                                          0,
                                                          true
                                                        )}`;
                                                      } else {
                                                        return `${k}: ${v}`;
                                                      }
                                                    })
                                                    .join(", ")
                                                : typeof value === "number"
                                                ? formats.formatNumber(
                                                    value,
                                                    xAtivo.decimais || 0,
                                                    true
                                                  )
                                                : value}
                                            </Typography>
                                          )
                                        )}
                                      </Stack>
                                    }
                                    arrow={true}
                                  >
                                    <IconButton color="primary" size="small">
                                      <Icon iconName="information" size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {xAtivo.ativos && (
                                  <Tooltip title="Ativos">
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() =>
                                        onSetEmissor({
                                          ...xAtivo,
                                          criterio: xItem.criterio,
                                          descricao: xItem.descricao,
                                          grafico: {
                                            range: xEnquadramentoRange,
                                            minimo: xItem.minimo,
                                            alvo: xAlvo,
                                            observado: xObservado,
                                            enquadrado: isEnquadrado,
                                          },
                                        })
                                      }
                                    >
                                      <Icon iconName="box_3d" size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color: xAlvo.targetColor,
                              }}
                            >
                              {xAtivo.observado &&
                                formats.formatNumber(xAtivo.observado, 6, true)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "info.main", fontWeight: 600 }}
                            >
                              {xItem.alvo && formats.formatNumber(xItem.alvo)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: xAlvo.targetColor,
                              }}
                            >
                              {hasDesenquadrado
                                ? formats.formatNumber(
                                    xAtivo.desenquadrado ||
                                      xAtivo.desenquadrado_alvo,
                                    xAtivo.decimais || 0,
                                    true
                                  )
                                : ""}
                            </TableCell>
                            <TableCell align="center">
                              {xAtivo.observado && (
                                <ConcentracaoGraph
                                  range={xEnquadramentoRange}
                                  minimo={xItem.minimo}
                                  alvo={xAlvo}
                                  observado={xObservado}
                                  targetColor={xAlvo.targetColor}
                                />
                              )}
                            </TableCell>
                            <TableCell align={xAtivo.erro ? "left" : "center"}>
                              {xAtivo.erro && (
                                <Tooltip title={xAtivo.erro} arrow>
                                  <IconButton color="warning" size="small">
                                    <Icon iconName="warning" size={21} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {xAtivo.nao_validado && (
                                <Tooltip title={"Não Validado"} arrow>
                                  <IconButton color="info" size="small">
                                    <Icon iconName="information" size={21} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default DetalheTable;
